import React, { useEffect, useCallback } from 'react';
import { Typography } from 'antd';
import { CheckOutlined, ClockCircleOutlined, CalendarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBookingFlowStore } from '../../../store/bookingFlowStore';
import { useUpcomingBookingStore } from '../../../store/upcomingBookingStore';
import { useMainButton } from '../../../hooks/useMainButton';
import { useHaptic } from '../../../hooks/useHaptic';
import type { CreateBookingRes } from '../../../types/client';

interface Props {
  result: CreateBookingRes;
}

export const SuccessScreen: React.FC<Props> = ({ result }) => {
  const { t } = useTranslation('customer');
  const navigate = useNavigate();
  const reset = useBookingFlowStore((s) => s.reset);
  const refresh = useUpcomingBookingStore((s) => s.refresh);
  const haptic = useHaptic();

  const isInstant = result.confirmationModel === 'INSTANT';

  useEffect(() => {
    haptic.notifySuccess();
    void refresh();
  }, [haptic, refresh]);

  const handleGoToBookings = useCallback(() => {
    reset();
    navigate('/bookings');
  }, [reset, navigate]);

  const handleGoHome = useCallback(() => {
    reset();
    navigate('/');
  }, [reset, navigate]);

  useMainButton(t('bookingFlow.success.goToBookingsBtn'), handleGoToBookings, true);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '40px 24px 24px',
      minHeight: '60vh',
      textAlign: 'center',
    }}>
      {/* Icon circle */}
      <div style={{
        width: 80, height: 80, borderRadius: 'var(--bl-radius-lg)',
        background: isInstant ? 'rgba(31,139,76,0.10)' : 'rgba(51,144,236,0.10)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 20,
      }}>
        {isInstant ? (
          <div style={{
            width: 52, height: 52, borderRadius: 'var(--bl-radius-lg)',
            background: 'rgba(31,139,76,1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, color: '#fff',
          }}>
            <CheckOutlined />
          </div>
        ) : (
          <div style={{
            width: 52, height: 52, borderRadius: 'var(--bl-radius-lg)',
            background: 'var(--bl-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, color: '#fff',
          }}>
            <ClockCircleOutlined />
          </div>
        )}
      </div>

      {/* Title */}
      <Typography.Text style={{
        display: 'block', fontSize: 22, fontWeight: 700,
        color: 'var(--bl-text)',
        letterSpacing: -0.3, marginBottom: 8,
      }}>
        {isInstant ? t('bookingFlow.success.instantTitle') : t('bookingFlow.success.approvalTitle')}
      </Typography.Text>

      {/* Subtitle */}
      <Typography.Text style={{
        display: 'block', fontSize: 13,
        color: 'var(--bl-text-secondary)',
        lineHeight: 1.6, maxWidth: 280,
        marginBottom: 32,
      }}>
        {isInstant
          ? t('bookingFlow.success.instantSubtitle')
          : t('bookingFlow.success.approvalSubtitle')}
      </Typography.Text>

      {/* Info card */}
      <div style={{
        width: '100%',
        borderRadius: 'var(--bl-radius-lg)',
        background: isInstant ? 'rgba(31,139,76,0.10)' : 'rgba(51,144,236,0.10)',
        border: '1px solid var(--bl-divider)',
        padding: '14px 16px',
        display: 'flex', alignItems: 'center', gap: 12,
        marginBottom: 24, textAlign: 'left',
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 'var(--bl-radius-sm)', flexShrink: 0,
          background: isInstant ? 'rgba(31,139,76,1)' : 'var(--bl-primary)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, color: '#fff',
        }}>
          <CalendarOutlined />
        </div>
        <Typography.Text style={{
          fontSize: 13, fontWeight: 500,
          color: isInstant ? 'rgba(31,139,76,1)' : 'var(--bl-primary)',
        }}>
          {isInstant
            ? t('bookingFlow.success.instantCard')
            : t('bookingFlow.success.approvalCard')}
        </Typography.Text>
      </div>

      {/* Go home button */}
      <button
        type="button"
        onClick={handleGoHome}
        style={{
          width: '100%',
          height: 48, borderRadius: 'var(--bl-radius-lg)',
          background: 'var(--bl-surface)',
          border: '1px solid var(--bl-divider)',
          color: 'var(--bl-text-secondary)',
          fontSize: 15, fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {t('bookingFlow.success.goHomeBtn')}
      </button>
    </div>
  );
};
